/**
 * marketplace/matching.ts — pure Roamer Match Score™ + smart recommendations.
 *
 * Personalized compatibility, computed honestly: each factor contributes
 * ONLY when there is recorded data to evaluate it (a partner without
 * pricing cannot be budget-scored; a user without recorded languages
 * cannot be language-matched). The final score normalizes over the
 * factors actually evaluated, and is null when none were. Smart
 * recommendations derive from the recorded catalog + recorded context
 * (current country, recorded needs) with a non-empty basis; deterministic
 * ids. Consent gating (personalizedRecommendations) lives in the module.
 */

import {
  ARRIVAL_CATEGORIES,
  type MarketplacePartner,
  type MarketplacePreferences,
  type MarketplaceReport,
  type MatchScoreBreakdown,
  type RoamerMatchScore,
  type SmartRecommendation,
  type UsageRecord,
} from './models';

const eq = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();
const overlap = (a: string[], b: string[]) => a.filter((x) => b.some((y) => eq(x, y)));

/** Average recorded rating for a partner — null when nothing is rated. */
export function communityRating(reports: MarketplaceReport[], partnerId: string): number | null {
  const rated = reports.filter((r) => r.partnerId === partnerId && r.rating != null);
  if (rated.length === 0) return null;
  const avg = rated.reduce((s, r) => s + (r.rating as number), 0) / rated.length;
  return Math.round(avg * 10) / 10;
}

export interface MatchContext {
  currentCountry: string | null;
  preferences: MarketplacePreferences;
  reports: MarketplaceReport[];
  usage: UsageRecord[];
}

export function computeMatchScore(partner: MarketplacePartner, ctx: MatchContext): RoamerMatchScore {
  const breakdown: MatchScoreBreakdown[] = [];
  const add = (factor: MatchScoreBreakdown['factor'], score: number, basis: string) =>
    breakdown.push({ factor, score: Math.max(0, Math.min(100, Math.round(score))), basis });

  // Location — evaluable only when both sides have recorded coverage data.
  if (ctx.currentCountry != null && partner.supportedCountries.length > 0) {
    const covered = partner.supportedCountries.some((c) => eq(c, ctx.currentCountry!));
    add('location', covered ? 100 : 0,
      covered ? `covers ${ctx.currentCountry}` : `no recorded coverage for ${ctx.currentCountry}`);
  }

  // Budget — needs a recorded budget AND recorded pricing in the same currency.
  const { monthlyBudget, budgetCurrency } = ctx.preferences;
  if (monthlyBudget != null && budgetCurrency != null &&
      partner.pricing.amount != null && partner.pricing.currency != null &&
      eq(partner.pricing.currency, budgetCurrency)) {
    const monthly = partner.pricing.period === 'yearly'
      ? partner.pricing.amount / 12
      : partner.pricing.amount; // oneTime/monthly compared as-is
    const within = monthly <= monthlyBudget;
    add('budget', within ? 100 : Math.max(0, 100 - ((monthly - monthlyBudget) / monthlyBudget) * 100),
      `${monthly.toFixed(0)} ${partner.pricing.currency}/mo vs budget ${monthlyBudget} ${budgetCurrency}`);
  }

  // Language — both sides need recorded languages.
  if (ctx.preferences.languages.length > 0 && partner.availableLanguages.length > 0) {
    const shared = overlap(ctx.preferences.languages, partner.availableLanguages);
    add('language', shared.length > 0 ? 100 : 0,
      shared.length > 0 ? `shared language: ${shared.join(', ')}` : 'no shared language recorded');
  }

  // Community feedback — recorded ratings only.
  const rating = communityRating(ctx.reports, partner.id);
  if (rating != null) {
    add('communityFeedback', ((rating - 1) / 4) * 100, `community rating ${rating}/5`);
  }

  // Previous usage — the user's own recorded outcomes.
  const uses = ctx.usage.filter((u) => u.partnerId === partner.id);
  if (uses.length > 0) {
    const positive = uses.filter((u) => u.outcome === 'positive').length;
    const negative = uses.filter((u) => u.outcome === 'negative').length;
    add('previousUsage', ((positive - negative) / uses.length + 1) * 50,
      `${positive} positive / ${negative} negative of ${uses.length} recorded uses`);
  }

  // Current needs — recorded needs matching the partner's category.
  if (ctx.preferences.currentNeeds.length > 0) {
    const needed = ctx.preferences.currentNeeds.includes(partner.category);
    add('currentNeeds', needed ? 100 : 0,
      needed ? `matches current need: ${partner.category}` : 'not among recorded current needs');
  }

  // Travel style — a recorded style paired with recorded pricing tier.
  if (ctx.preferences.travelStyle != null && partner.pricing.amount != null) {
    // Honest heuristic over recorded data: budget style favors lower prices.
    const style = ctx.preferences.travelStyle;
    const score = style === 'balanced' ? 70 : style === 'budget'
      ? (partner.pricing.amount <= 50 ? 100 : 40)
      : (partner.pricing.amount >= 50 ? 90 : 60);
    add('travelStyle', score, `recorded style '${style}' vs recorded price ${partner.pricing.amount}`);
  }

  if (breakdown.length === 0) {
    return { partnerId: partner.id, score: null, breakdown: [], evaluatedFactors: 0 };
  }
  const score = Math.round(breakdown.reduce((s, b) => s + b.score, 0) / breakdown.length);
  return { partnerId: partner.id, score, breakdown, evaluatedFactors: breakdown.length };
}

export interface RecommendationContext extends MatchContext {
  partners: MarketplacePartner[];
}

/**
 * Contextual recommendations: arrival bundle (eSIM, airport transfer,
 * travel insurance, translation) for partners covering the current
 * country, plus partners matching recorded current needs. Deterministic
 * ids; every recommendation carries recorded basis.
 */
export function computeRecommendations(ctx: RecommendationContext): SmartRecommendation[] {
  const recommendations: SmartRecommendation[] = [];
  const seen = new Set<string>();
  const push = (partner: MarketplacePartner, message: string, basis: string[]) => {
    const id = `rec:${partner.id}:${basis[0]}`;
    if (seen.has(id)) return;
    seen.add(id);
    recommendations.push({
      id, partnerId: partner.id, partnerName: partner.name,
      category: partner.category, message, basis,
    });
  };

  if (ctx.currentCountry != null) {
    for (const partner of ctx.partners) {
      if (!ARRIVAL_CATEGORIES.includes(partner.category)) continue;
      if (!partner.supportedCountries.some((c) => eq(c, ctx.currentCountry!))) continue;
      push(partner,
        `You're in ${ctx.currentCountry} — ${partner.name} (${partner.category}) covers it.`,
        [`arrival category ${partner.category}`, `recorded coverage for ${ctx.currentCountry}`]);
    }
  }

  for (const need of ctx.preferences.currentNeeds) {
    for (const partner of ctx.partners) {
      if (partner.category !== need) continue;
      push(partner,
        `${partner.name} matches your recorded need: ${need}.`,
        [`recorded current need ${need}`]);
    }
  }

  return recommendations;
}
